<script lang="ts">
  import { scale } from '@lib/directives';
  import { onMount } from 'svelte';
  // Props with defaults
  export let label: string | undefined = undefined;
  export let loading: boolean = false;
  export let disabled: boolean = false;
  export let size: 'micro' | 'sm' | 'md' | 'lg' = 'md';
  export let variant:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'link'
    | 'skip'
    | 'submit'
    | 'rounded'
    | 'actionPrimary'
    | 'actionSecondary'
    | 'actionTertiary'
    | 'subtle'
    | 'icon'
    | 'icon-box' = 'info';
  export let color: string | undefined = undefined;
  export let bgColor: string | undefined = undefined;
  export let inline: boolean = false;
  export let animate = false;
  export let bold = false;
  export let scaleAmount: number | undefined = undefined;

  let buttonContainer: HTMLElement;

  onMount(() => {
    // Set up container query observer
    const observer = new ResizeObserver(() => {
      const btn = buttonContainer.querySelector('.btn') as HTMLElement;
      if (btn) {
        // Force removal of transform when container size changes
        btn.style.transform = '';
      }
    });

    observer.observe(buttonContainer);

    return () => {
      observer.disconnect();
    };
  });

  // Compute classes based on props
  $: classes = [
    'btn',
    `btn--${size}`,
    `btn--${variant}`,
    loading && 'btn--loading',
    disabled && 'btn--disabled',
    inline && 'btn--inline',
    animate && 'animate',
    bold && 'btn--bold',
    (bgColor || color) && 'btn--custom-colors',
  ]
    .filter(Boolean)
    .join(' ');

  // Compute styles for custom colors
  $: style = [color && `--btn-color: ${color}`, bgColor && `--btn-bg-color: ${bgColor}`]
    .filter(Boolean)
    .join(';');
</script>

<div class="button-container" class:inline bind:this={buttonContainer}>
  <button
    class={classes}
    {disabled}
    {style}
    use:scale={{ scale: scaleAmount || 1.15, duration: 200 }}
    on:click
  >
    {#if loading}
      <span class="loader"></span>
    {/if}
    <slot>{label}</slot>
  </button>
</div>

<style lang="postcss">
  .button-container {
    container-type: inline-size;
    container-name: button;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .button-container.inline {
    display: inline-flex;
    width: auto;
    container-type: normal; /* Disable container queries for inline buttons */
  }

  /* CSS Custom Properties for theming */
  button {
    --btn-primary-bg: var(--theme-primary-color);
    --btn-primary-color: var(--theme-text-on-primary);
    --btn-secondary-bg: var(--theme-secondary-color);
    --btn-secondary-color: var(--theme-text-on-secondary);
    --btn-tertiary-bg: var(--theme-tertiary-color);
    --btn-tertiary-color: var(--theme-text-on-tertiary);
    --btn-success-bg: var(--success-color, #10b981);
    --btn-success-color: var(--text-on-success, white);
    --btn-warning-bg: var(--warning-color, #f59e0b);
    --btn-warning-color: var(--text-on-warning, white);
    --btn-danger-bg: var(--danger-color, #ef4444);
    --btn-danger-color: var(--text-on-danger, white);
    --btn-disabled-opacity: var(--disabled-opacity);
    --btn-transition: var(--transition);
    --btn-radius: var(--radius);
    font-family: var(--font-family);
  }

  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    border: none;
    border-radius: var(--radius);
    cursor: pointer;
    font-family: inherit;
    transition: all var(--btn-transition);
    position: relative;
    will-change: transform;
    white-space: nowrap;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    transform: scale(1);
    touch-action: manipulation;
  }

  @container button (max-width: 280px) {
    .btn {
      transform-origin: center;
      font-size: 0.75rem;
    }

    .button-container .btn.btn--link.btn--link {
      transform: none;
      font-size: 0.625rem;
    }
  }

  .label {
    white-space: nowrap;
  }

  /* Sizes */
  .btn--micro {
    font-size: 0.8em;
    padding: 0.15rem 0.4rem;
    height: 1.5rem;
    border-radius: 2px !important; /* Force override */
  }

  .btn--sm {
    padding: 0.5rem;
    font-size: 0.975rem;
    height: 2rem;
    --radius: 5px;
  }

  .btn--md {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    height: 2.5rem;
  }

  .btn--lg {
    padding: 1rem 2rem;
    font-size: 1.125rem;
    height: 3rem;
  }

  /* Variants */
  .btn--primary {
    background-color: var(--btn-bg-color, var(--btn-primary-bg));
    color: var(--btn-color, var(--btn-primary-color));
  }

  .btn--primary:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  .btn--secondary {
    background-color: var(--btn-secondary-bg);
    color: var(--btn-secondary-color);
  }

  .btn--secondary:hover:not(:disabled) {
    /* Using modern color-mix() function with fallback */
    background-color: color-mix(in srgb, var(--btn-secondary-bg) 85%, white);
    @supports not (color-mix(in srgb, black, white)) {
      /* Fallback for browsers that don't support color-mix */
      filter: brightness(1.15);
    }
  }

  .btn--tertiary {
    background-color: var(--btn-tertiary-bg);
    color: var(--btn-tertiary-color);
  }

  .btn--tertiary:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  .btn--info {
    background-color: var(--btn-bg-color, #3b82f6);
    color: var(--btn-color, white);
  }

  .btn--info:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  /* States */
  .btn--disabled,
  .btn:disabled {
    opacity: var(--btn-disabled-opacity);
    cursor: not-allowed;
  }

  /* Loading state */
  .btn--loading {
    cursor: wait;
  }

  .hidden {
    opacity: 0;
  }

  /* Loading spinner */
  .loader {
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    animation: rotation 1s linear infinite;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  @keyframes rotation {
    0% {
      transform: translateX(-50%) rotate(0deg);
    }
    100% {
      transform: translateX(-50%) rotate(360deg);
    }
  }

  /* New variants */
  .btn--success {
    background-color: var(--btn-bg-color, var(--btn-success-bg, #10b981));
    color: var(--btn-color, var(--btn-success-color, white));
  }

  .btn--success:hover:not(:disabled) {
    background-color: var(
      --btn-bg-color,
      color-mix(in srgb, var(--btn-success-bg, #10b981) 85%, white)
    );
    @supports not (color-mix(in srgb, black, white)) {
      filter: brightness(1.15);
    }
  }

  .btn--danger {
    background-color: var(--btn-bg-color, var(--btn-danger-bg, #ef4444));
    color: var(--btn-color, var(--btn-danger-color, white));
  }

  .btn--danger:hover:not(:disabled) {
    background-color: var(
      --btn-bg-color,
      color-mix(in srgb, var(--btn-danger-bg, #ef4444) 85%, white)
    );
    @supports not (color-mix(in srgb, black, white)) {
      filter: brightness(1.15);
    }
  }

  .btn--warning {
    background-color: var(--btn-bg-color, var(--btn-warning-bg, #f59e0b));
    color: var(--btn-color, var(--btn-warning-color, white));
  }

  .btn--warning:hover:not(:disabled) {
    background-color: var(
      --btn-bg-color,
      color-mix(in srgb, var(--btn-warning-bg, #f59e0b) 85%, white)
    );
    @supports not (color-mix(in srgb, black, white)) {
      filter: brightness(1.15);
    }
  }

  /* Add custom focus styles */
  .btn:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px var(--bg-color, white),
      0 0 0 4px var(--btn-primary-bg);
  }

  /* Remove default focus ring */
  .btn:focus {
    outline: none;
  }

  /* Remove default active state */
  .btn:active {
    outline: none;
  }

  /* Link variant - increase specificity with .button-container */
  .button-container .btn.btn--link {
    background: none;
    border: none;
    padding: 0;
    height: auto;
    color: var(--text-color);
    opacity: 0.7;
    border-bottom: 1px dotted currentColor;
    border-radius: 0;
    font-weight: bold;
    font-size: 0.95rem;
  }

  .button-container .btn.btn--link:hover:not(:disabled) {
    opacity: 1;
    background: none;
    filter: none;
  }

  /* Add new variants */
  .btn--skip {
    background: none;
    border: none;
    padding: 0;
    height: auto;
    color: var(--text-color);
    opacity: 0.7;
    border-bottom: 1px solid currentColor;
    border-radius: 0;
    font-size: 1.1rem;
  }

  .btn--skip:hover:not(:disabled) {
    opacity: 1;
    background: none;
    filter: none;
  }

  /* Submit variant */
  .btn--submit {
    background-color: var(--btn-bg-color, var(--btn-success-bg, #10b981));
    color: var(--btn-color, var(--btn-success-color, white));
    border-radius: 5px;
    font-size: 1.1rem;
  }

  .btn--submit:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  /* Skip variant */
  .button-container .btn.btn--skip {
    background: none;
    border: none;
    padding: 0;
    height: auto;
    color: var(--text-color);
    opacity: 0.7;
    border-bottom: 1px solid currentColor;
    border-radius: 0;
    font-size: 1.1rem;
  }

  .button-container .btn.btn--skip:hover:not(:disabled) {
    opacity: 1;
    background: none;
    filter: none;
  }

  /* Action variants */
  .btn--actionPrimary {
    background-color: var(--btn-bg-color, var(--btn-primary-bg));
    color: var(--btn-color, var(--btn-primary-color));
    border: none;
    border-radius: 5px;
  }

  .btn--actionPrimary:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  .btn--actionSecondary {
    background-color: var(--theme-secondary-color, var(--btn-secondary-bg));
    color: var(--btn-color, var(--btn-secondary-color));
    border: none;
    border-radius: 5px;
  }

  .btn--actionSecondary:hover:not(:disabled) {
    background-color: var(
      --theme-secondary-color,
      color-mix(in srgb, var(--btn-secondary-bg) 85%, white)
    );
    @supports not (color-mix(in srgb, black, white)) {
      filter: brightness(1.15);
    }
  }

  .btn--actionTertiary {
    background-color: var(--theme-tertiary-color, var(--btn-tertiary-bg));
    color: var(--btn-color, var(--btn-tertiary-color));
    border: none;
    border-radius: 5px;
  }

  .btn--actionTertiary:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  .btn--rounded {
    border-radius: 100%;
    aspect-ratio: 1 !important;
  }
  .btn--rounded {
    background-color: var(--btn-bg-color, var(--btn-primary-bg));
    color: var(--btn-color, var(--btn-primary-color));
  }

  .btn--rounded:hover:not(:disabled) {
    filter: brightness(1.1);
  }

  /* Subtle variant - minimal gray button */
  .btn--subtle {
    background-color: #f3f4f6;
    color: #1f2309;
    border: 1px solid #e5e7eb;
  }

  .btn--subtle:hover:not(:disabled) {
    filter: brightness(0.95);
  }

  /* Bold modifier */
  .btn--bold {
    font-weight: bold;
  }

  /* Custom colors modifier - allows bgColor/color props to override variant defaults */
  .btn--custom-colors {
    background-color: var(--btn-bg-color);
    color: var(--btn-color);
  }

  /* Icon variant - for icon-only buttons */
  .btn--icon {
    background: none;
    border: none;
    padding: 0 !important;
    height: auto;
    color: var(--btn-color, var(--theme-text-color));
    border-radius: 0.25rem;
  }

  .btn--icon:hover:not(:disabled) {
    background: none;
    filter: brightness(1.3);
  }

  .btn--icon:focus-visible {
    box-shadow: 0 0 0 2px var(--theme-primary-color);
  }

  /* Icon-box variant - icon-only buttons with proper visual weight */
  .btn--icon-box {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--btn-color, #6b7280);
    border: none;
    padding: 0.375rem !important;
    height: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius);
    border-radius: 5px;
  }

  .btn--icon-box:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.2);
    color: var(--btn-color, #4b5563);
    border-color: var(--border-color, #d1d5db);
  }

  .btn--icon-box:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px var(--bg-color, white),
      0 0 0 4px var(--theme-primary-color);
  }
</style>
